import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, message, Table } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useNavigate } from 'react-router-dom';
import {
  createAppointment,
  fetchAdminCreator,
  openAppointmentRoom
} from 'services/api/appointments';
import { createMeetingRoom } from 'services/api/meetings';
import { AppointmentListRow } from 'types/api/appointments';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import useCopyText from 'hooks/useCopyText';
import { StyledLink } from './styled';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private, OrganizationAlert } from 'components/shared';

const PAGE_LIMIT = 10;

const Sessions: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { copyText } = useCopyText();
  const [links, setLinks] = useState<Record<string, string>>({});
  const [passwords, setPasswords] = useState<Record<string, string>>({});

  const [results, setResults] = useState<AppointmentListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // cycle-29: admin-created free sessions = creator='1' AND paid=false.
  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAdminCreator(
          {
            page,
            limit: PAGE_LIMIT
          },
          true
        );

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const onCreateSession = useCallback(async () => {
    try {
      setIsCreating(true);
      await createAppointment({});
      message.success(t('messages.successfully_created'));
      setPage(1);
      setRefreshKey(k => k + 1);
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsCreating(false);
    }
  }, [t]);

  const onCopyRoom = useCallback(
    async (id: string) => {
      try {
        const room = await openAppointmentRoom(id);
        if (!room?.redirect_url) return;
        const link = `${room.redirect_url}/home/${room.meetUrl}`;
        setLinks(prev => ({ ...prev, [id]: link }));
        copyText(link);

        const meeting = await createMeetingRoom(id);
        setPasswords(prev => ({ ...prev, [id]: meeting.password }));
      } catch (err) {
        showErrorMessage(err);
      }
    },
    [copyText]
  );

  const columns: ColumnProps<AppointmentListRow>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: id => id
      },
      {
        title: 'Hash',
        dataIndex: 'hashId',
        key: 'hashId',
        render: hashId => hashId
      },
      {
        title: 'Type',
        dataIndex: 'patientPaymentType',
        key: 'patientPaymentType',
        render: patientPaymentType => patientPaymentType
      },
      {
        title: t('sessions.link'),
        dataIndex: 'id',
        key: 'link',
        width: '30%',
        render: (id, record) => {
          // Freshly generated wins; otherwise the room stored on the row, so
          // the link survives a refresh.
          const link = links[id] ?? record.link_share?.url;

          return link ? (
            <StyledLink onClick={() => copyText(link)}>{link}</StyledLink>
          ) : (
            '-'
          );
        }
      },
      {
        title: 'Password',
        dataIndex: 'id',
        key: 'password',
        render: (id, record) => {
          const password = passwords[id] ?? record.link_share?.password;

          return password ? (
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => copyText(password)}
            >
              {password}
            </span>
          ) : (
            '-'
          );
        }
      },
      {
        title: 'Created at',
        dataIndex: 'created_at',
        key: 'created_at',
        width: '25%',
        render: createdAt => dayjs(createdAt).format(FULL_DATE_FORMAT)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        width: '5%',
        align: 'right',
        render: id => (
          <ActionMenu
            data={[
              {
                title: 'Generate link and password',
                permission: {
                  route: PERMISSION_ROUTES.sessions,
                  action: PERMISSION_ACTIONS.generateUrl
                },
                action() {
                  onCopyRoom(id);
                }
              },
              {
                title: 'Logs',
                permission: {
                  route: PERMISSION_ROUTES.super,
                  action: PERMISSION_ACTIONS.read
                },
                action() {
                  navigate(`/logs/meetings?search=${id}`);
                }
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [copyText, links, navigate, onCopyRoom, passwords, t]
  );

  return (
    <PageWrapper
      title="Free Calls"
      color="white"
      extra={[
        <Private
          key="add-sessions-button"
          route={PERMISSION_ROUTES.sessions}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button
              type="primary"
              onClick={onCreateSession}
              loading={isCreating}
            >
              <PlusOutlined />
              {t('sessions.create')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Sessions;
